import Quill from "quill";
import React, { useEffect, useRef, useState } from "react";

import { getHtml } from "../getHtml";

const blockClosingTagPattern =
  /(<\/(?:address|article|aside|blockquote|details|dialog|dd|div|dl|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|hgroup|hr|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul|li)>)/gi;

function formatHtmlForSourceViewer(html: string): string {
  return html.replace(blockClosingTagPattern, "$1\n").trim();
}

interface RichTextSourceViewerProps {
  quill: Quill | null;
  value: string;
  onChange: (value: string) => void;
  onModeChange: (showSource: boolean) => void;
}

export const RichTextSourceViewer: React.FC<RichTextSourceViewerProps> = ({ quill, value, onChange, onModeChange }) => {
  const [showSource, setShowSource] = useState(false);
  const [sourceHtml, setSourceHtml] = useState(value);
  const sourceHtmlRef = useRef(sourceHtml);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    sourceHtmlRef.current = sourceHtml;
  }, [sourceHtml]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!quill) return;

    const toolbar = quill.container.previousElementSibling as HTMLElement | null;
    const sourceButton = toolbar?.querySelector("button.ql-source");
    if (!sourceButton) return;

    sourceButton.innerHTML = "<b>&lt;/&gt;</b>";
    sourceButton.setAttribute("aria-label", "HTML source");

    const toggleSource = () => {
      setShowSource((current) => {
        if (current) {
          const delta = quill.clipboard.convert({ html: sourceHtmlRef.current });
          quill.setContents(delta);
          const newValue = getHtml(quill);
          const formattedValue = formatHtmlForSourceViewer(newValue);
          sourceHtmlRef.current = formattedValue;
          setSourceHtml(formattedValue);
          onChangeRef.current(newValue);
        } else {
          const formattedValue = formatHtmlForSourceViewer(getHtml(quill));
          sourceHtmlRef.current = formattedValue;
          setSourceHtml(formattedValue);
        }

        onModeChange(!current);
        return !current;
      });
    };

    sourceButton.addEventListener("click", toggleSource);
    return () => sourceButton.removeEventListener("click", toggleSource);
  }, [onModeChange, quill]);

  useEffect(() => {
    if (showSource && value !== sourceHtmlRef.current) {
      const formattedValue = formatHtmlForSourceViewer(value);
      sourceHtmlRef.current = formattedValue;
      setSourceHtml(formattedValue);
    }
  }, [showSource, value]);

  return (
    <textarea
      className="ql-source-viewer"
      value={sourceHtml}
      aria-label="HTML source"
      onChange={(event) => {
        const newValue = event.target.value;
        sourceHtmlRef.current = newValue;
        setSourceHtml(newValue);
        onChangeRef.current(newValue);
      }}
    />
  );
};
